/**
 * tableUtils.js - Centralized table filtering and sorting utilities
 *
 * Provides reusable functions for filtering, sorting, and processing table data.
 * Designed to work with React class components and auto-generate filter options.
 */

/**
 * Gets a nested value from an object using dot notation or array path
 * @param {Object} obj - The object to extract from
 * @param {string|Array} path - Property path (e.g., "user.name" or ["user", "name"])
 * @returns {*} The value at the path, or undefined
 */
export function getNestedValue(obj, path) {
  if (!obj || !path) return undefined;

  const pathArray = Array.isArray(path) ? path : path.split(".");

  return pathArray.reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
}

/**
 * Extracts unique filter options from data for a given column path
 * @param {Array} data - Array of data objects
 * @param {string} path - The property path to extract values from
 * @param {Object} options - Configuration options
 * @param {boolean} options.includeAll - Whether to prepend "All" option (default: true)
 * @param {string} options.allLabel - Label for the "All" option (default: "All")
 * @param {function} options.transform - Optional transform function for values
 * @param {function} options.sortFn - Optional custom sort function
 * @returns {Array} Array of unique values for filter dropdown
 */
export function extractFilterOptions(data, path, options = {}) {
  const {
    includeAll = true,
    allLabel = "All",
    transform = (val) => val,
    sortFn = (a, b) => String(a).localeCompare(String(b)),
  } = options;

  if (!data || !Array.isArray(data) || data.length === 0) {
    return includeAll ? [allLabel] : [];
  }

  const uniqueValues = new Set();

  data.forEach((item) => {
    const value = getNestedValue(item, path);
    if (value !== undefined && value !== null && value !== "") {
      uniqueValues.add(transform(value));
    }
  });

  const sortedValues = Array.from(uniqueValues).sort(sortFn);

  return includeAll ? [allLabel, ...sortedValues] : sortedValues;
}

/**
 * Applies multiple filters to a dataset
 * @param {Array} data - Array of data objects
 * @param {Array} filters - Array of filter configurations
 * @param {string} filters[].path - Property path to filter on
 * @param {*} filters[].value - Value to filter by (can be single value or array for multi-select)
 * @param {string} filters[].type - Filter type: "exact", "contains", "startsWith", "custom"
 * @param {string} filters[].allValue - Value that represents "all" (no filter) - default: "All"
 * @param {function} filters[].customFn - Custom filter function (for type: "custom")
 * @returns {Array} Filtered data array
 */
export function applyFilters(data, filters) {
  if (!data || !Array.isArray(data)) return [];
  if (!filters || filters.length === 0) return data;

  return data.filter((item) => {
    return filters.every((filter) => {
      const { path, value, type = "exact", allValue = "All", customFn } = filter;

      // Skip filter if value matches the "all" value or is empty
      if (value === allValue || value === "" || value === undefined) return true;

      // Handle multi-select (array of values)
      if (Array.isArray(value)) {
        // Empty array means no filter applied (show all)
        if (value.length === 0) return true;

        const itemValue = getNestedValue(item, path);

        // For multi-select, item matches if its value is in the selected array
        switch (type) {
          case "exact":
            return value.includes(itemValue);

          case "contains":
            return value.some((v) =>
              String(itemValue || "")
                .toLowerCase()
                .includes(String(v).toLowerCase())
            );

          case "startsWith":
            return value.some((v) =>
              String(itemValue || "")
                .toLowerCase()
                .startsWith(String(v).toLowerCase())
            );

          case "custom":
            return customFn ? customFn(item, value) : true;

          default:
            return value.includes(itemValue);
        }
      }

      // Single value filtering (original behavior)
      const itemValue = getNestedValue(item, path);

      switch (type) {
        case "exact":
          return itemValue === value;

        case "contains":
          return String(itemValue || "")
            .toLowerCase()
            .includes(String(value).toLowerCase());

        case "startsWith":
          return String(itemValue || "")
            .toLowerCase()
            .startsWith(String(value).toLowerCase());

        case "custom":
          return customFn ? customFn(item, value) : true;

        default:
          return itemValue === value;
      }
    });
  });
}

/**
 * Applies a single filter to data (convenience wrapper)
 * @param {Array} data - Array of data objects
 * @param {string} path - Property path to filter on
 * @param {*} value - Value to filter by
 * @param {string} type - Filter type (default: "exact")
 * @param {string} allValue - Value representing "all" (default: "All")
 * @returns {Array} Filtered data array
 */
export function filterData(data, path, value, type = "exact", allValue = "All") {
  return applyFilters(data, [{ path, value, type, allValue }]);
}

/**
 * Sorts data by a column configuration
 * @param {Array} data - Array of data objects
 * @param {Object} sortColumn - Sort configuration
 * @param {string} sortColumn.path - Property path to sort by
 * @param {string} sortColumn.order - Sort order: "asc" or "desc"
 * @param {Object} columnConfig - Optional column configuration with custom sort
 * @returns {Array} Sorted data array (new array, original unchanged)
 */
export function sortData(data, sortColumn, columnConfig = null) {
  if (!data || !Array.isArray(data) || data.length === 0) return [];
  if (!sortColumn || !sortColumn.path) return [...data];

  const { path, order = "asc" } = sortColumn;
  const multiplier = order === "asc" ? 1 : -1;

  // Check if column has custom sort function
  if (columnConfig && columnConfig.sortFn) {
    return [...data].sort((a, b) => columnConfig.sortFn(a, b) * multiplier);
  }

  // Detect column type once from the first non-null value instead of
  // running Date.parse on every comparison (which is very slow at scale).
  let colType = "string";
  for (let i = 0; i < data.length; i++) {
    const sample = getNestedValue(data[i], path);
    if (sample != null) {
      if (typeof sample === "number") {
        colType = "number";
      } else if (!isNaN(Date.parse(sample))) {
        colType = "date";
      }
      break;
    }
  }

  return [...data].sort((a, b) => {
    const aValue = getNestedValue(a, path);
    const bValue = getNestedValue(b, path);

    // Handle null/undefined values (push to end)
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;

    if (colType === "number") {
      return (aValue - bValue) * multiplier;
    }

    if (colType === "date") {
      return (Date.parse(aValue) - Date.parse(bValue)) * multiplier;
    }

    // Default to string comparison
    return String(aValue).localeCompare(String(bValue)) * multiplier;
  });
}

/**
 * Combined filter, sort, and paginate operation
 * @param {Array} data - Array of data objects
 * @param {Object} options - Processing options
 * @param {Array} options.filters - Array of filter configurations
 * @param {Object} options.sortColumn - Sort configuration
 * @param {Object} options.columnConfig - Column configuration for custom sort
 * @param {number} options.currentPage - Current page number (1-indexed)
 * @param {number} options.pageSize - Items per page
 * @returns {Object} { totalCount: number, data: Array }
 */
export function processTableData(data, options = {}) {
  const {
    filters = [],
    sortColumn = null,
    columnConfig = null,
    currentPage = 1,
    pageSize = 10,
  } = options;

  if (!data || !Array.isArray(data)) {
    return { totalCount: 0, data: [] };
  }

  // Step 1: Apply filters
  let processed = applyFilters(data, filters);

  // Step 2: Apply sorting
  if (sortColumn && sortColumn.path) {
    processed = sortData(processed, sortColumn, columnConfig);
  }

  // Step 3: Get total count before pagination
  const totalCount = processed.length;

  // Step 4: Apply pagination
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = processed.slice(startIndex, startIndex + pageSize);

  return { totalCount, data: paginatedData };
}

/**
 * Builds filter configuration from column definitions and current filter state
 * @param {Array} columns - Array of column definitions with filter metadata
 * @param {Object} filterState - Current filter values keyed by column filterKey
 * @returns {Array} Array of filter configurations for applyFilters()
 */
export function buildFiltersFromColumns(columns, filterState) {
  if (!columns || !filterState) return [];

  return columns
    .filter((col) => {
      if (!col.filterable) return false;
      const key = col.filterKey || col.path || col.key;
      const value = filterState[key];
      return value !== undefined && value !== null;
    })
    .map((col) => {
      const key = col.filterKey || col.path || col.key;
      return {
        path: col.path || col.key,
        value: filterState[key],
        type: col.filterType || "exact",
        allValue: col.filterAllValue || "All",
        customFn: col.filterFn,
      };
    });
}
